import React, { ReactNode, useEffect, useState } from "react";

import { hasInterestRateChart } from "../../../../helpers/depositType";
import { TInvestmentProductDetails } from "../CreateInvestmentProduct";
import FormSection from "./FormSection";
import Notice from "./Notice";
import CurrencyDetailSection from "./CurrencyDetailSection";
import styles from "./fundForm.module.scss";

type TLocks = Partial<Record<keyof TInvestmentProductDetails, string>>;

interface IFinancialsSection {
  details: TInvestmentProductDetails[];
  onChangeDetail: (
    detail: TInvestmentProductDetails,
    patch: Partial<TInvestmentProductDetails>,
  ) => void;
  /**
   * The currency multi-select, rendered in the header.
   *
   * It belongs beside the tabs it fills rather than in a card of its own:
   * choosing a currency and configuring it are one job, and a separate
   * "Currencies" card put a whole section between the two.
   */
  currencyPicker?: ReactNode;
  locks?: TLocks;
  /** Whether this fund is live — see `CurrencyDetailSection`. */
  isLive?: boolean;
  step?: string;
}

const keyOf = (detail: TInvestmentProductDetails) =>
  String(detail?.id ?? detail?.value ?? detail?.currencyCode ?? "");

/** Whether a currency has everything creation needs. Drives the tab's dot. */
function isComplete(detail: TInvestmentProductDetails): boolean {
  const base =
    Boolean(detail?.coreBankingTenantId) &&
    Boolean(detail?.coreBankingProductId) &&
    Number(detail?.minimumInvestment) > 0 &&
    Number(detail?.maximumInvestment) > 0 &&
    Boolean(detail?.investmentInterestPaymentTypeId) &&
    Boolean(detail?.termsAndConditionId);

  if (!hasInterestRateChart(detail?.depositType)) {
    return base;
  }

  return base && Boolean(detail?.interestRateChart?.chartSlabs?.length);
}

/**
 * The currencies a fund accepts, one tab each.
 *
 * They used to stack, so a two-currency fund was two identical walls of
 * fields and it took scrolling to tell which one you were in. Tabs also make
 * an incomplete currency visible from the other one — the dot on a tab says
 * that currency is not finished yet.
 */
export default function FinancialsSection({
  details,
  onChangeDetail,
  currencyPicker,
  locks,
  isLive,
  step,
}: IFinancialsSection) {
  const [activeKey, setActiveKey] = useState(() =>
    keyOf(details[0] ?? ({} as any)),
  );

  useEffect(() => {
    // A currency can be removed from under the open tab.
    if (
      details.length &&
      !details.some((detail) => keyOf(detail) === activeKey)
    ) {
      setActiveKey(keyOf(details[0]));
    }
  }, [details, activeKey]);

  const active =
    details.find((detail) => keyOf(detail) === activeKey) ?? details[0];

  if (!details.length) {
    return (
      <FormSection
        id="financials"
        title="Financials"
        subtitle="Set up each currency this fund accepts."
        action={currencyPicker}
      >
        <Notice tone="info">
          Choose a currency to set up its accounts and rates.
        </Notice>
      </FormSection>
    );
  }

  return (
    <FormSection
      id="financials"
      title="Financials"
      subtitle="Set up each currency this fund accepts."
      bare
      action={
        <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
          {currencyPicker}
          {step ? <span className={styles.sectionMeta}>{step}</span> : null}
        </div>
      }
    >
      {details.length ? (
        <div className={styles.tabs} role="tablist">
          {details.map((detail) => {
            const key = keyOf(detail);
            const label =
              detail?.label || detail?.currencyCode || detail?.value;
            const complete = isComplete(detail);

            return (
              <button
                key={key}
                type="button"
                role="tab"
                aria-selected={key === activeKey}
                className={`${styles.tab} ${
                  key === activeKey ? styles.tabOn : ""
                }`}
                onClick={() => setActiveKey(key)}
              >
                {label}
                {hasInterestRateChart(detail?.depositType) ? (
                  <span className={styles.tabChip}>Savings</span>
                ) : null}
                {!complete ? (
                  <span
                    className={styles.tabDot}
                    title="Not finished yet"
                    aria-label="Not finished yet"
                  />
                ) : null}
              </button>
            );
          })}
        </div>
      ) : null}

      {active ? (
        <CurrencyDetailSection
          key={keyOf(active)}
          detail={active}
          locks={locks}
          isLive={isLive}
          onChange={(patch) => onChangeDetail(active, patch)}
        />
      ) : null}
    </FormSection>
  );
}
